import { useSelector } from "react-redux";
import { ActionButton, ActionLink } from "@wine2trace/ui/FormComponents";
import useLabels from "../../hooks/useLabels";
import { selectActiveWinery, selectConfigs } from "../../context/selectors";
import { buildAlertPresentation } from "../../services/eventAlertsPresenter";
import { automationUrl } from "../../services/automationUrl";
import { Icon } from "../Icon";

// Alerts listed on the Home; the full list is one link away.
const MAX_ALERTS = 4;

const SEVERITY_LABEL_KEYS = {
  critical: "eventAlertSeverityCritical",
  warning: "eventAlertSeverityWarning",
  info: "eventAlertSeverityInfo",
};

// Explicit action per alert category: a link says what it does, never just "open".
const ACTION_LABEL_KEYS = {
  OPERATIONS: "homeAlertActionOperation",
  ANALYSIS: "homeAlertActionAnalysis",
  WAREHOUSE: "homeAlertActionWarehouse",
  FILTER: "homeAlertActionFilter",
};

// The IoT alerts are listed in automation: here only their count (summary.iot_alerts, null when the server
// could not count them) and the link to the alerts page of automation on the same winery.
const HomeIotAlerts = ({ iotAlerts, wineryId, getLabel }) => {
  const severity = iotAlerts?.critical > 0 ? "critical" : "warning";
  return (
    <div className={`home_alerts_item severity_${severity}`} data-testid="home-iot-alerts">
      <div className="home_alerts_head">
        <Icon icon="fa-triangle-exclamation" className="home_alerts_icon" aria-hidden="true" />
        <span className="home_alerts_title">{getLabel("homeIotAlertsTitle")}</span>
      </div>
      <p className="home_alerts_message">
        {iotAlerts
          ? getLabel("homeIotAlertsCount", { active: iotAlerts.active, critical: iotAlerts.critical })
          : getLabel("homeIotAlertsUnavailable")}
      </p>
      <ActionLink href={automationUrl("/alerts", wineryId)} variant="inline" icon="fa-up-right-from-square">
        {getLabel("homeIotAlertsOpen")}
      </ActionLink>
    </div>
  );
};

// "Richiede attenzione": the most urgent active enology alerts, and the count of the IoT ones. Which alerts
// reach this user is decided by the server (summary.alerts is absent without the ALERTS permission,
// summary.iot_alerts also without IOT_READ).
const HomeAttentionPanel = ({ summary, status, onRetry }) => {
  const [getLabel] = useLabels();
  const configs = useSelector(selectConfigs);
  const activeWinery = useSelector(selectActiveWinery);

  if (status === "idle") return null;

  const title = getLabel("homeAttentionTitle");

  if (status === "loading") {
    return (
      <section className="home_panel home_panel_loading" aria-label={title}>
        <div data-testid="home-attention-loading" aria-busy="true" className="home_panel_placeholder" />
      </section>
    );
  }

  const alerts = summary?.alerts;
  const iotAlerts = summary?.iot_alerts;
  const failed = status === "error" || alerts === null;
  const showIot = status !== "error" && (iotAlerts === null || iotAlerts?.active > 0);
  if (!failed && alerts === undefined && !showIot) return null;

  return (
    <section className="home_panel" aria-label={title}>
      <h4 className="home_panel_title">{title}</h4>

      {failed ? (
        <div className="home_panel_error">
          <p>{getLabel("eventAlertsLoadError")}</p>
          <ActionButton icon="fa-rotate" onClick={onRetry}>
            {getLabel("homeSummaryRetry")}
          </ActionButton>
        </div>
      ) : alerts === undefined ? null : alerts.active === 0 ? (
        !showIot && <p className="home_panel_empty">{getLabel("eventAlertsEmpty")}</p>
      ) : (
        <ul className="home_alerts">
          {(alerts.items || []).slice(0, MAX_ALERTS).map((alert) => {
            const presentation = buildAlertPresentation(alert, getLabel, {
              dateFormat: configs?.shortDateFormat,
            });
            const severity = (presentation.severity || "").toLowerCase();
            const severityKey = SEVERITY_LABEL_KEYS[severity];
            return (
              <li key={alert.id} className={`home_alerts_item severity_${severity}`}>
                <div className="home_alerts_head">
                  <Icon icon={presentation.icon} className="home_alerts_icon" aria-hidden="true" />
                  <span className="home_alerts_title">{presentation.title}</span>
                  {severityKey && <span className="home_alerts_severity">{getLabel(severityKey)}</span>}
                </div>
                <p className="home_alerts_message">{presentation.message}</p>
                <ActionLink to={presentation.deepLink || "/alerts"} variant="inline" icon="fa-arrow-right">
                  {getLabel(ACTION_LABEL_KEYS[alert.category] || "homeAlertActionDefault")}
                </ActionLink>
              </li>
            );
          })}
        </ul>
      )}

      {showIot && <HomeIotAlerts iotAlerts={iotAlerts} wineryId={activeWinery?.id} getLabel={getLabel} />}

      {(failed || alerts !== undefined) && (
        <ActionLink to="/alerts" variant="more">
          {getLabel("eventAlertsViewAll")}
        </ActionLink>
      )}
    </section>
  );
};

export default HomeAttentionPanel;
